import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AuthPage from "./AuthPage.jsx";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getPasswordChecks = (password) => [
  password.length >= 8,
  /[A-Z]/.test(password),
  /[a-z]/.test(password),
  /\d/.test(password),
  /[^A-Za-z0-9]/.test(password),
];

export default function SignUpForm({ onSubmit, onCancel, error, loading }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const passwordChecks = getPasswordChecks(password);
  const passwordIsValid = passwordChecks.every(Boolean);
  const emailIsValid = isValidEmail(email);
  const passwordsMatch = password === confirmPassword;
  const formIsValid =
    name.trim() && emailIsValid && passwordIsValid && passwordsMatch;

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (!formIsValid) return;

    onSubmit({
      name: name.trim(),
      email: email.trim().toLowerCase(),
      password,
    });
  };

  const inputClassName = (hasError) =>
    `mt-1 block w-full rounded-lg border bg-white px-4 py-3 text-gray-900 shadow-sm outline-none transition focus:ring-2 ${
      hasError
        ? "border-red-500 focus:border-red-500 focus:ring-red-200"
        : "border-gray-300 focus:border-sky-600 focus:ring-sky-200"
    }`;

  return (
    <AuthPage
      title={t("registrationTitle")}
      subtitle={t("registrationSubtitle")}
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <div>
          <label
            htmlFor="registration-name"
            className="text-sm font-medium text-slate-700"
          >
            {t("fullName")}
          </label>
          <input
            id="registration-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={inputClassName(submitted && !name.trim())}
            disabled={loading}
            required
          />
          {submitted && !name.trim() && (
            <p className="mt-1 text-sm text-red-600">{t("nameRequired")}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="registration-email"
            className="text-sm font-medium text-slate-700"
          >
            {t("email")}
          </label>
          <input
            id="registration-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={inputClassName(submitted && !emailIsValid)}
            disabled={loading}
            required
          />
          {submitted && !emailIsValid && (
            <p className="mt-1 text-sm text-red-600">{t("emailNotValid")}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="registration-password"
            className="text-sm font-medium text-slate-700"
          >
            {t("passw")}
          </label>
          <input
            id="registration-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={inputClassName(submitted && !passwordIsValid)}
            disabled={loading}
            required
          />
          <ul className="mt-2 space-y-1 text-xs text-slate-600">
            {[
              t("passwordMinLength"),
              t("passwordUppercase"),
              t("passwordLowercase"),
              t("passwordNumber"),
              t("passwordSymbol"),
            ].map((label, index) => (
              <li
                key={label}
                className={passwordChecks[index] ? "text-emerald-700" : ""}
              >
                {passwordChecks[index] ? "✓" : "•"} {label}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <label
            htmlFor="registration-confirm-password"
            className="text-sm font-medium text-slate-700"
          >
            {t("confrmPassw")}
          </label>
          <input
            id="registration-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            className={inputClassName(submitted && !passwordsMatch)}
            disabled={loading}
            required
          />
          {submitted && !passwordsMatch && (
            <p className="mt-1 text-sm text-red-600">{t("notMatch")}</p>
          )}
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 p-3 text-sm text-red-700"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg bg-sky-600 px-4 py-3 font-semibold text-white transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? t("creatingAccount") : t("createAcount")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="w-full text-sm font-medium text-sky-700 underline hover:text-sky-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {t("backToLogin")}
        </button>
      </form>
    </AuthPage>
  );
}
